// HubSpot Forms API Integration
// Documentation: https://developers.hubspot.com/docs/api/marketing/forms

interface HubSpotFormData {
  firstName: string;
  lastName: string;
  email: string;
  company: string;
  jobTitle: string;
  companySize: string;
  challenge?: string;
  // Hidden tracking fields
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  referrer_url?: string;
  landing_page_url?: string;
  signup_timestamp?: string;
  device_type?: string;
  theme_preference?: string;
  preferred_language?: string;
  // Quiz fields
  quiz_score?: number;
  quiz_tier?: 'starter' | 'strategist' | 'leader';
  quiz_completed?: boolean;
  source?: string;
}

// Replace these with your actual HubSpot Portal ID and Form GUID
const HUBSPOT_PORTAL_ID = "146971137";
const HUBSPOT_FORM_GUID = "eb0ee366-c85f-4323-b536-31e2b06a6104";

export const submitToHubSpot = async (data: HubSpotFormData): Promise<boolean> => {
  const url = `https://api.hsforms.com/submissions/v3/integration/submit/${HUBSPOT_PORTAL_ID}/${HUBSPOT_FORM_GUID}`;

  // Map form data to HubSpot field format
  const fields = [
    { name: "firstname", value: data.firstName },
    { name: "lastname", value: data.lastName },
    { name: "email", value: data.email },
    { name: "company", value: data.company },
    { name: "jobtitle", value: data.jobTitle },
    { name: "company_size", value: data.companySize },
  ];

  // Add optional challenge field
  if (data.challenge) {
    fields.push({ name: "sustainability_challenge", value: data.challenge });
  }

  // Add hidden tracking fields
  if (data.utm_source) fields.push({ name: "utm_source", value: data.utm_source });
  if (data.utm_medium) fields.push({ name: "utm_medium", value: data.utm_medium });
  if (data.utm_campaign) fields.push({ name: "utm_campaign", value: data.utm_campaign });
  if (data.referrer_url) fields.push({ name: "referrer_url", value: data.referrer_url });
  if (data.landing_page_url) fields.push({ name: "landing_page_url", value: data.landing_page_url });
  if (data.signup_timestamp) fields.push({ name: "signup_timestamp", value: data.signup_timestamp });
  if (data.device_type) fields.push({ name: "device_type", value: data.device_type });
  if (data.theme_preference) fields.push({ name: "theme_preference", value: data.theme_preference });
  if (data.preferred_language) fields.push({ name: "preferred_language", value: data.preferred_language });
  
  // Add quiz fields
  if (data.quiz_score !== undefined) fields.push({ name: "quiz_score", value: data.quiz_score.toString() });
  if (data.quiz_tier) fields.push({ name: "quiz_tier", value: data.quiz_tier });
  if (data.quiz_completed) fields.push({ name: "quiz_completed", value: "true" });
  if (data.source) fields.push({ name: "source", value: data.source });

  const payload = {
    fields,
    context: {
      pageUri: window.location.href,
      pageName: document.title,
    },
  };

  try {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorData = await response.json();
      console.error("HubSpot API error:", errorData);
      throw new Error(`HubSpot submission failed: ${response.status}`);
    }

    if (import.meta.env.DEV) {
      console.log("✅ Successfully submitted to HubSpot");
    }
    return true;
  } catch (error) {
    console.error("❌ Error submitting to HubSpot:", error);
    throw error;
  }
};

/**
 * Get current waitlist count from HubSpot
 * 
 * SECURITY TRADE-OFF ACCEPTED:
 * This uses a client-side API key (VITE_HUBSPOT_API_KEY) which is visible
 * in the production JavaScript bundle. This is an accepted risk because:
 * 
 * 1. The key has READ-ONLY permissions (configured in HubSpot dashboard)
 * 2. It only fetches aggregate count, not individual contact data
 * 3. HubSpot has built-in rate limiting to prevent abuse
 * 4. Key can be rotated if compromised (low impact)
 * 5. UX benefit (real-time social proof) outweighs low risk
 * 
 * ALTERNATIVE APPROACH (if security requirements change):
 * - Create Lovable Cloud Edge Function to proxy this request
 * - Store HUBSPOT_API_KEY as a secret (not VITE_ prefix)
 * - Frontend calls your Edge Function (no exposed key)
 * 
 * See: docs/05-guides/SECURITY_REVIEW.md
 * 
 * @returns Current waitlist count or 0 on error
 */
export const getWaitlistCount = async (): Promise<number> => {
  try {
    // SECURITY: This API key will be visible in client-side code
    // Ensure it has read-only permissions in HubSpot dashboard
    const apiKey = import.meta.env.VITE_HUBSPOT_API_KEY;
    
    if (!apiKey) {
      console.warn('HubSpot API key not configured - using placeholder count');
      return 0;
    }

    const response = await fetch(
      'https://api.hubapi.com/contacts/v1/lists/all/contacts/all?count=1',
      {
        headers: {
          'Authorization': `Bearer ${apiKey}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error(`HubSpot API error: ${response.status}`);
    }

    const data = await response.json();
    return data.total || 0;
  } catch (error) {
    console.error('Failed to fetch waitlist count:', error);
    return 0; // Fallback to 0 on error
  }
};
